import { useCallback, useEffect, useState } from 'react';
import { useRecaptcha } from './ReCAPTCHAContext';
import {
  type Attribute,
  type Tag,
  useScriptTags,
} from '@legalzoom/react-utils';

// Hook that loads the reCAPTCHA v3 script tag
const useLoadRecaptcha = () => {
  const { recaptchaV3Key, recaptchaEnabled } = useRecaptcha();

  const recaptchaScriptTag: Attribute[] = [
    [
      'src',
      `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaV3Key}`,
    ],
  ];

  const tags: Tag[] = recaptchaEnabled ? [recaptchaScriptTag] : [];

  useScriptTags({ tags });
};

// Helper func that sets the visibility of the reCAPTCHA badge
const setBadgeVisibility = (visible: boolean) => {
  const recaptchaBadge = document.querySelector(
    '.grecaptcha-badge',
  ) as HTMLElement | null;
  if (recaptchaBadge) {
    recaptchaBadge.style.visibility = visible ? 'visible' : 'hidden';
    recaptchaBadge.style.display = visible ? 'block' : 'none';
  }
};

// Helper func that waits for reCAPTCHA to load
const waitForRecaptcha = (): Promise<void> => {
  return new Promise((resolve) => {
    if (window.grecaptcha?.enterprise) {
      resolve();
      return;
    }

    const interval = setInterval(() => {
      if (window.grecaptcha?.enterprise) {
        clearInterval(interval);
        resolve();
      }
    }, 100);
  });
};

// Helper func that executes the reCAPTCHA and returns a token
const executeRecaptchaToken = async (
  recaptchaV3Key: string,
  action: string,
): Promise<string> => {
  await waitForRecaptcha();
  return new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      resolve(
        await window.grecaptcha.enterprise.execute(recaptchaV3Key, { action }),
      );
    });
  });
};

// Hook that executes the reCAPTCHA and returns a token
const useExecuteRecaptcha = (action: string, isSubmitting: boolean) => {
  const [token, setToken] = useState<string>('');
  const { recaptchaV3Key, recaptchaEnabled } = useRecaptcha();

  const executeRecaptcha = useCallback(async () => {
    if (!recaptchaEnabled) return;

    try {
      const token = await executeRecaptchaToken(recaptchaV3Key, action);
      setBadgeVisibility(true);
      setToken(token);
    } catch (error) {
      console.error('Error executing reCAPTCHA:', error);
    }
  }, [recaptchaV3Key, action, setToken]);

  useEffect(() => {
    // Execute on mount and hide badge during cleanup
    executeRecaptcha();
    return () => setBadgeVisibility(false);
  }, [executeRecaptcha]);

  useEffect(() => {
    // Execute when isSubmitting changes
    if (isSubmitting) {
      executeRecaptcha();
    }
  }, [isSubmitting, executeRecaptcha]);

  return token;
};

export { useLoadRecaptcha, useExecuteRecaptcha };
